/**
 * @fileoverview Primitives chart GNPC basées sur Recharts.
 *
 * Ces helpers reprennent le pattern shadcn `ChartContainer` :
 * - le `config` centralise les labels et couleurs de séries ;
 * - les couleurs sont exposées en variables CSS `--color-*` par graphique ;
 * - le tooltip reprend les composants DS (`border`, `bg-popover`, radius 8 px)
 *   sans ombre, conformément aux règles GNPC.
 *
 * Utiliser ces primitives pour les graphiques denses de dashboard. Le code
 * métier reste libre d'utiliser `BarChart`, `AreaChart`, `XAxis`, etc. depuis
 * `recharts`, mais la boîte, les couleurs et le tooltip doivent passer par ici.
 */

import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import { cn } from '../../lib/utils.ts';

const THEMES = { light: '', dark: '.dark' } as const;

export type ChartConfig = {
  [key: string]: {
    /** Label lisible affiché dans tooltip, légende ou sélecteur. */
    label?: React.ReactNode;
    /** Icône optionnelle si la série doit être identifiée visuellement. */
    icon?: React.ComponentType;
  } & (
    | {
        /** Couleur unique de série, ex : `var(--chart-1)`. */
        color?: string;
        theme?: never;
      }
    | {
        /** Couleur différente par thème. */
        color?: never;
        theme: Record<keyof typeof THEMES, string>;
      }
  );
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart(): ChartContextProps {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }

  return context;
}

interface ChartContainerProps extends React.ComponentProps<'div'> {
  /** Configuration de séries : labels, couleurs et icônes. */
  config: ChartConfig;
  /** Identifiant stable optionnel, utile pour tests visuels ou docs. */
  id?: string;
  /** Graphe Recharts à rendre dans un `ResponsiveContainer`. */
  children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
}

/**
 * Conteneur standard pour Recharts.
 *
 * @param props - `config`, children Recharts et props de `<div>`.
 * @returns Wrapper responsive avec variables CSS de séries.
 *
 * @example
 * ```tsx
 * <ChartContainer config={{ paid: { label: 'Payés', color: 'var(--chart-1)' } }}>
 *   <BarChart data={rows}>
 *     <Bar dataKey="paid" fill="var(--color-paid)" />
 *   </BarChart>
 * </ChartContainer>
 * ```
 */
function ChartContainer({
  id,
  className,
  children,
  config,
  ...props
}: ChartContainerProps): React.JSX.Element {
  const uniqueId = React.useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, '')}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          'flex aspect-video justify-center text-xs',
          '[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground',
          '[&_.recharts-cartesian-grid_line[stroke="#ccc"]]:stroke-border/70',
          '[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border',
          '[&_.recharts-dot[stroke="#fff"]]:stroke-transparent',
          '[&_.recharts-layer]:outline-none',
          '[&_.recharts-sector]:outline-none',
          '[&_.recharts-sector[stroke="#fff"]]:stroke-transparent',
          '[&_.recharts-surface]:outline-none',
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

interface ChartStyleProps {
  id: string;
  config: ChartConfig;
}

function ChartStyle({ id, config }: ChartStyleProps): React.JSX.Element | null {
  const colorConfig = Object.entries(config).filter(
    ([, itemConfig]) => itemConfig.theme || itemConfig.color,
  );

  if (!colorConfig.length) return null;

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(([theme, prefix]) => {
            const cssVariables = colorConfig
              .map(([key, itemConfig]) => {
                const color =
                  itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ?? itemConfig.color;
                return color ? `  --color-${key}: ${color};` : null;
              })
              .filter(Boolean)
              .join('\n');

            return `${prefix} [data-chart="${id}"] {\n${cssVariables}\n}`;
          })
          .join('\n'),
      }}
    />
  );
}

const ChartTooltip = RechartsPrimitive.Tooltip;

interface ChartTooltipPayloadItem {
  name?: string | number;
  dataKey?: string | number;
  value?: string | number | Array<string | number>;
  color?: string;
  fill?: string;
  payload?: Record<string, unknown>;
}

interface ChartTooltipContentProps extends React.ComponentProps<'div'> {
  active?: boolean;
  payload?: ChartTooltipPayloadItem[];
  label?: string | number;
  hideLabel?: boolean;
  hideIndicator?: boolean;
  indicator?: 'line' | 'dot' | 'dashed';
  nameKey?: string;
  labelKey?: string;
  labelFormatter?: (value: string | number, payload?: ChartTooltipPayloadItem[]) => React.ReactNode;
  formatter?: (
    value: NonNullable<ChartTooltipPayloadItem['value']>,
    name: string | number | undefined,
    item: ChartTooltipPayloadItem,
    index: number,
    payload: ChartTooltipPayloadItem[],
  ) => React.ReactNode;
  /**
   * Props injectées par Recharts quand ce composant est passé à
   * `<Tooltip content={…}>`. Pas des attributs DOM valides : déclarées
   * uniquement pour être extraites avant le spread `{...props}` (sinon
   * React logge « unknown DOM attribute » — même piège que
   * `ChartLegendContent`).
   *
   * Recharts 3.x clone le contenu avec la TOTALITÉ de ses props résolues
   * (`defaultTooltipProps` : itemStyle, wrapperStyle, separator, etc.),
   * pas seulement celles liées à l'état actif — d'où la liste complète
   * ci-dessous. Le tooltip ne se rendant qu'au survol, ces fuites
   * n'apparaissent pas dans un audit statique : ne pas réduire la liste.
   */
  activeIndex?: number;
  accessibilityLayer?: boolean;
  coordinate?: { x?: number; y?: number };
  offset?: number;
  allowEscapeViewBox?: { x?: boolean; y?: boolean };
  animationDuration?: number;
  animationEasing?: string;
  axisId?: string | number;
  contentStyle?: React.CSSProperties;
  cursor?: unknown;
  filterNull?: boolean;
  includeHidden?: boolean;
  isAnimationActive?: boolean | 'auto';
  itemSorter?: unknown;
  itemStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  position?: { x?: number; y?: number };
  reverseDirection?: { x?: boolean; y?: boolean };
  separator?: string;
  trigger?: 'hover' | 'click';
  useTranslate3d?: boolean;
  wrapperStyle?: React.CSSProperties;
}

/**
 * Tooltip standard pour `ChartTooltip`.
 *
 * @param props - Props transmises par Recharts + options d'affichage GNPC.
 * @returns Contenu de tooltip aligné avec les surfaces DS.
 */
const ChartTooltipContent = React.forwardRef<HTMLDivElement, ChartTooltipContentProps>(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelKey,
      nameKey,
      formatter,
      // Extraites pour ne pas atteindre le <div> DOM — voir ChartTooltipContentProps.
      activeIndex: _activeIndex,
      accessibilityLayer: _accessibilityLayer,
      coordinate: _coordinate,
      offset: _offset,
      allowEscapeViewBox: _allowEscapeViewBox,
      animationDuration: _animationDuration,
      animationEasing: _animationEasing,
      axisId: _axisId,
      // `content` : Recharts réinjecte l'élément lui-même dans ses props.
      content: _content,
      contentStyle: _contentStyle,
      cursor: _cursor,
      filterNull: _filterNull,
      includeHidden: _includeHidden,
      isAnimationActive: _isAnimationActive,
      itemSorter: _itemSorter,
      itemStyle: _itemStyle,
      labelStyle: _labelStyle,
      position: _position,
      reverseDirection: _reverseDirection,
      separator: _separator,
      trigger: _trigger,
      useTranslate3d: _useTranslate3d,
      wrapperStyle: _wrapperStyle,
      ...props
    },
    ref,
  ) => {
    const { config } = useChart();

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) return null;

      const [item] = payload;
      const key = `${labelKey ?? item?.dataKey ?? item?.name ?? 'value'}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      const value =
        labelKey && typeof item?.payload?.[labelKey] !== 'undefined'
          ? `${item.payload[labelKey]}`
          : label;

      if (labelFormatter && typeof value !== 'undefined') {
        return labelFormatter(value, payload);
      }

      return labelKey ? (itemConfig?.label ?? value) : value;
    }, [config, hideLabel, label, labelFormatter, labelKey, payload]);

    if (!active || !payload?.length) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'border-border bg-popover text-popover-foreground grid min-w-32 gap-1.5 rounded-lg border px-3 py-2 text-xs',
          className,
        )}
        {...props}
      >
        {tooltipLabel ? <div className="font-medium">{tooltipLabel}</div> : null}
        <div className="grid gap-1.5">
          {payload.map((item, index) => {
            const key = `${nameKey ?? item.name ?? item.dataKey ?? 'value'}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = item.color ?? item.fill;

            return (
              <div key={`${key}-${index}`} className="flex min-w-0 flex-wrap items-center gap-2">
                {formatter && typeof item.value !== 'undefined' ? (
                  formatter(item.value, item.name, item, index, payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : !hideIndicator ? (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'shrink-0 rounded-[2px] border-[--color-border] bg-[--color-bg]',
                          indicator === 'dot' && 'size-2.5',
                          indicator === 'line' && 'h-0.5 w-3',
                          indicator === 'dashed' &&
                            'h-0 w-3 border-t-2 border-dashed bg-transparent',
                        )}
                        style={
                          {
                            '--color-bg': indicatorColor,
                            '--color-border': indicatorColor,
                          } as React.CSSProperties
                        }
                      />
                    ) : null}
                    <span className="text-muted-foreground min-w-0 flex-1">
                      {itemConfig?.label ?? item.name}
                    </span>
                    {typeof item.value !== 'undefined' ? (
                      <span className="text-foreground font-mono font-medium tabular-nums">
                        {Array.isArray(item.value)
                          ? item.value.join(' – ')
                          : Number.isFinite(Number(item.value))
                            ? Number(item.value).toLocaleString('fr-FR')
                            : item.value}
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
ChartTooltipContent.displayName = 'ChartTooltipContent';

const ChartLegend = RechartsPrimitive.Legend;

interface ChartLegendPayloadItem {
  value?: string | number;
  dataKey?: string | number;
  color?: string;
  payload?: Record<string, unknown>;
}

interface ChartLegendContentProps extends React.ComponentProps<'div'> {
  payload?: readonly ChartLegendPayloadItem[];
  nameKey?: string;
  /**
   * Props injectées par Recharts quand ce composant est passé à
   * `<Legend content={…}>`. Elles ne sont PAS des attributs DOM valides :
   * on les déclare uniquement pour les retirer du spread `{...props}`
   * (sinon React logge « unknown DOM attribute » pour chacune).
   *
   * Recharts 3.x clone le contenu avec la TOTALITÉ des props résolues de
   * `Legend` (`legendDefaultProps` + dimensions calculées), d'où la liste
   * complète ci-dessous — ne pas la réduire aux seules props visibles dans
   * un audit ponctuel.
   */
  verticalAlign?: 'top' | 'middle' | 'bottom';
  chartWidth?: number;
  chartHeight?: number;
  /** Couleur des séries inactives — injectée par Recharts, jamais rendue ici. */
  inactiveColor?: string;
  /** Tri des items de légende — injecté par Recharts, jamais rendu ici. */
  itemSorter?: string | ((item: ChartLegendPayloadItem) => number | string);
  /** Style de label hérité du Tooltip Recharts — jamais rendu ici. */
  labelStyle?: React.CSSProperties;
  align?: 'left' | 'center' | 'right';
  iconSize?: number;
  layout?: 'horizontal' | 'vertical';
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  width?: number;
  height?: number;
  payloadUniqBy?: unknown;
}

/**
 * Légende standard pour les graphiques GNPC.
 *
 * @param props - Payload Recharts et clé métier optionnelle utilisée pour résoudre les labels.
 * @returns Légende responsive utilisant les couleurs et libellés du `ChartConfig`.
 *
 * @example
 * ```tsx
 * <ChartLegend content={<ChartLegendContent nameKey="method" />} />
 * ```
 */
function ChartLegendContent({
  className,
  payload,
  nameKey,
  // Extraites pour ne pas atteindre le <div> DOM — voir ChartLegendContentProps.
  verticalAlign: _verticalAlign,
  chartWidth: _chartWidth,
  chartHeight: _chartHeight,
  inactiveColor: _inactiveColor,
  itemSorter: _itemSorter,
  labelStyle: _labelStyle,
  align: _align,
  iconSize: _iconSize,
  layout: _layout,
  margin: _margin,
  width: _width,
  height: _height,
  // `content` : Recharts réinjecte l'élément lui-même dans ses props.
  content: _content,
  payloadUniqBy: _payloadUniqBy,
  ...props
}: ChartLegendContentProps): React.JSX.Element | null {
  const { config } = useChart();

  if (!payload?.length) return null;

  return (
    <div
      className={cn('flex items-center justify-center gap-4', className)}
      {...props}
    >
      {payload.map((item, index) => {
        const payloadKey = nameKey ? item.payload?.[nameKey] : undefined;
        const key = `${payloadKey ?? item.dataKey ?? item.value ?? index}`;
        const itemConfig = config[key];
        const markerColor = item.color ?? item.payload?.fill;

        return (
          <div key={`${key}-${index}`} className="flex min-w-0 items-center gap-2 text-xs">
            <span
              aria-hidden="true"
              className="size-2.5 shrink-0 rounded-[2px]"
              style={{ backgroundColor: typeof markerColor === 'string' ? markerColor : undefined }}
            />
            <span className="text-muted-foreground truncate">
              {itemConfig?.label ?? item.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: ChartTooltipPayloadItem | undefined,
  key: string,
): ChartConfig[string] | undefined {
  if (!payload) return config[key];

  const payloadPayload = payload.payload;
  let configLabelKey = key;

  if (key in payload && typeof payload[key as keyof ChartTooltipPayloadItem] === 'string') {
    configLabelKey = `${payload[key as keyof ChartTooltipPayloadItem]}`;
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key] === 'string') {
    configLabelKey = `${payloadPayload[key]}`;
  }

  return config[configLabelKey] ?? config[key];
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
};
